import { toast } from 'sonner';
import { useCanvasStore } from '@/stores/canvasStore';
import { watchExtensionJob } from '../jobs';

/** Fetch boards (the target may be new) and navigate to it. */
export async function openBoard(boardId: string) {
  await useCanvasStore.getState().fetchBoards();
  const board = useCanvasStore.getState().boards.find(b => b.id === boardId);
  if (board) await useCanvasStore.getState().navigateToBoard(board);
}

/**
 * Follow a Top Creatives run until its cards are on the board, then refresh
 * the board if it is open. Lives outside any component so it survives the
 * panel (or Partnr's chat) closing.
 *
 * `navigate: true` opens the new board straight away (the panel's own flow).
 * From Partnr we leave the user where they are and offer an "Open board" button.
 */
export async function followAnalysis(
  boardId: string,
  extensionJobId: string | undefined,
  { navigate }: { navigate: boolean },
) {
  const openAction = { label: 'Open board', onClick: () => { openBoard(boardId); } };
  if (navigate) await openBoard(boardId);

  if (!extensionJobId) return;
  const job = await watchExtensionJob(extensionJobId);
  if (!job) {
    toast.info('Top Creatives is still running. Reopen the board in a few minutes.', navigate ? undefined : { action: openAction });
    return;
  }
  if (job.status === 'failed') {
    toast.error(`Top Creatives failed: ${job.error_message ?? 'unknown error'}`);
    return;
  }
  const found = Number(job.result?.creatives_found ?? 0);
  const scraped = Number(job.result?.total_scraped ?? 0);
  toast.success(
    found > 0
      ? `Found ${found} top creatives from ${scraped} posts. Scores fill in over the next minute.`
      : `No posts cleared the engagement bar (${scraped} scraped). Try a lower threshold or longer window.`,
    { duration: navigate ? 6000 : 15000, ...(navigate || found === 0 ? {} : { action: openAction }) }
  );
  if (useCanvasStore.getState().currentBoardId === boardId) {
    await useCanvasStore.getState().fetchItems(boardId);
  }
}
